import React from 'react';
import { Grid, Box, Alert } from '@mui/material';
import { useRecoilState, useSetRecoilState } from 'recoil';
import Modal from 'components/modal';
import { customToast } from 'api/utils';
import { orderPayload, toggleSspModels, isPrivilegedADGroupValid } from 'pages/cloud-onboarding/store';
import { addContributorADGroup, removeContributorADGroup } from 'pages/cloud-onboarding/store/selector';
import { RI003Styles } from 'pages/reference-implementation/provisioning/RI003O/style';
import Btn from 'components/button';
import deleteIcon from 'assets/images/delete_icon.svg';
import addIcon from 'assets/images/add_icon.svg';
import ADGroupContributor from './ADGroupContributor';

const AwsPrivilegedADGroupModal = () => {
    const [order, setOrder] = useRecoilState(orderPayload);
    const [isOpen, setIsOpen] = useRecoilState(toggleSspModels);
    const addGroup = useSetRecoilState(addContributorADGroup);
    const removeGroup = useSetRecoilState(removeContributorADGroup);
    const [lastModifiedPG, setLastModifiedPG] = React.useState(undefined);
    const [lastPGValid, setLastPGValid] = React.useState(undefined);
    const [emptyContributorList, setEmptyContributorList] = React.useState([]);
    const [privilegedADGroupValid, setPrivilegedADGroupValid] = useRecoilState(isPrivilegedADGroupValid);

    const isFormValid = () => {
        let errorCount = 0;
        let isContributorADGroupValid = false;
        if (order?.privilegedADGroupList?.length) {
            order?.privilegedADGroupList?.forEach((adGroup) => {
                if (adGroup && (adGroup?.contributorValue === '' || adGroup?.contributorValue === null)) {
                    // eslint-disable-next-line no-plusplus
                    errorCount++;
                }
            });
        }
        isContributorADGroupValid = errorCount === 0;
        return isContributorADGroupValid;
    };
    const isDuplicateADGroup = () => {
        const contributorADGroupArray = order?.privilegedADGroupList?.map((item) => item?.contributorValue);
        return new Set(contributorADGroupArray).size !== contributorADGroupArray.length;
    };

    React.useEffect(() => {
        if (isOpen?.isPrivilegedADGroupOpen) {
            setPrivilegedADGroupValid(false);
            setLastPGValid(privilegedADGroupValid);
            setLastModifiedPG(order?.privilegedADGroupList);
            // setOpenInfo(true);
        }
    }, [isOpen?.isPrivilegedADGroupOpen]);

    const handleSubmit = async () => {
        const emptyArr = [];
        order?.privilegedADGroupList.forEach((z, i) => {
            if (z?.contributorValue === '' || z?.contributorValue === null) {
                emptyArr.push(i);
            }
        });
        setEmptyContributorList(emptyArr);

        // Validate form and check for duplicates
        if (!isFormValid() || isDuplicateADGroup()) {
            customToast(['AD Group cannot be empty or duplicate.']);
            return;
        }

        // If validation passes, save and close modal
        setPrivilegedADGroupValid(false);
        setTimeout(() => {
            setPrivilegedADGroupValid(true);
        }, 1000);
        setIsOpen({ ...isOpen, isPrivilegedADGroupOpen: false });
    };

    const updateEmptyIndexList = (indexVal) => {
        setEmptyContributorList(emptyContributorList?.filter((z) => z !== indexVal));
    };

    return (
        <Modal
            title="Privileged Active Directory Groups"
            handleClose={() => {
                setPrivilegedADGroupValid(lastPGValid);
                setIsOpen({ ...isOpen, isPrivilegedADGroupOpen: false });
                setOrder({
                    ...order,
                    privilegedADGroupList: lastModifiedPG,
                });
            }}
            open={isOpen.isPrivilegedADGroupOpen}
        >
            <Box sx={RI003Styles.modal.wrapper}>
                <Box sx={{ ...RI003Styles.modal.contentWrapper, paddingTop: '0px' }}>
                    <Grid container spacing={4} sx={{ marginBottom: '15px' }}>
                        <Grid item xs={12} sm={12}>
                            {/* {openInfo && ( */}
                            <Alert icon={false} severity="info" color="info" sx={RI003Styles.modal.alertBox}>
                                Maximum allowed Active Directory Groups are 5
                            </Alert>
                            {/* )} */}
                        </Grid>
                    </Grid>

                    {order?.privilegedADGroupList?.map((item, index) => (
                        <Box key={index.toString()} sx={RI003Styles.modal.rowWrapper} className="space-x-2 mb-5 animate-opacity">
                            <Box sx={RI003Styles.modal.groupWrapper}>
                                <React.Fragment key={index.toString()}>
                                    <ADGroupContributor indexVal={index} indexList={emptyContributorList} updateEmptyList={updateEmptyIndexList} />
                                </React.Fragment>
                            </Box>
                            <Box sx={RI003Styles.modal.iconWrapper}>
                                <Box className="flex w-full ">
                                    {index !== 0 && order?.privilegedADGroupList?.length > 1 && (
                                        <Box onClick={() => removeGroup(item)} sx={RI003Styles.modal.deleteWrapper}>
                                            <img style={RI003Styles.modal.delete} src={deleteIcon} alt="deleteIcon" />
                                        </Box>
                                    )}
                                    {index === order?.privilegedADGroupList.length - 1 && order?.privilegedADGroupList?.length < 5 ? (
                                        <Box
                                            sx={RI003Styles.modal.deleteWrapper}
                                            onClick={() => {
                                                addGroup({ count: 1 });
                                            }}
                                        >
                                            <img style={RI003Styles.modal.add} src={addIcon} alt="addIcon" />
                                        </Box>
                                    ) : (
                                        <span className="w-7 h-7" />
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    ))}
                </Box>
                <Box className="footer" sx={RI003Styles.modal.actionWrapper} gap={2}>
                    <Btn
                        className="custom-btn"
                        variant="contained"
                        onClick={() => {
                            handleSubmit();
                        }}
                        data-testid="save-btn"
                        sx={RI003Styles.modal.saveButton}
                    >
                        Save
                    </Btn>
                    <Btn
                        className="custom-btn"
                        sx={RI003Styles.modal.cancelButton}
                        onClick={() => {
                            setPrivilegedADGroupValid(lastPGValid);
                            setIsOpen({ ...isOpen, isPrivilegedADGroupOpen: false });
                            setOrder({
                                ...order,
                                privilegedADGroupList: lastModifiedPG,
                            });
                        }}
                        variant="outlined"
                        color="cmpWarning"
                        layout="outline"
                        data-testid="cancel-btn"
                    >
                        Cancel
                    </Btn>
                </Box>
            </Box>
        </Modal>
    );
};

export default AwsPrivilegedADGroupModal;
